import { motion, useInView, useScroll, useTransform } from "framer-motion"
import { useEffect, useRef, useState } from "react"
import { LuUserRound } from "react-icons/lu"
import { RiArtboardLine } from "react-icons/ri"
import quote from "../../images/icons/new/quote.svg"
import { Chip } from "../../myne/Chip/Chip"

// avatars
import { StaticImage } from "gatsby-plugin-image"
import useWindowDimensions from "../../utils/useWindowDimensions"

const caseStudyData = [
  {
    logo: (
      <StaticImage
        src="../../images/client-logos/latest/everything-middle-east.png"
        alt="Egypt Logo"
        width={150}
        placeholder="blurred"
      />
    ),
    title:
      "AI GITEX Egypt drove 2,000+ registrations with personalized AI avatar campaign.",
    features: [
      {
        name: "AI Avatars",
        link: "/avatar-studio/",
      },
      {
        name: "Advocacy Posters",
        link: "/create-event-marketing-posters/",
      },
    ],
    showType: "Trade Show",
    attendees: "50K+",
    heroImage: (
      <StaticImage
        src="../../images/case-study-covers/everything-egypt.png"
        alt="Event"
        className="w-full h-full object-cover rounded-3xl"
        formats={["auto", "webp", "avif"]}
        width={1200}
      />
    ),
    testimonial: {
      avatar: (
        <StaticImage
          src="../../images/case-study/ai-everything-mea/ahmed-alkhatib.jpeg"
          alt="ai-everything-mea-egypt-ai-summit-photo"
          className="w-full h-full object-cover rounded-3xl"
          width={800}
        />
      ),
      quote:
        "We started seeing avatars shared across LinkedIn, which organically increased visibility for the event. It gave attendees something interactive and creative beyond a typical email campaign.",
      name: "Ahmed Alkhatib",
      Company: "DWTC",
    },
  },
  {
    logo: (
      <StaticImage
        src="../../images/client-logos/latest/richmond.png"
        alt="Richmond Logo"
        width={100}
        placeholder="blurred"
      />
    ),
    title:
      "Richmond Italia made photo sharing effortless — attendees posted the same day.",
    features: [
      {
        name: "AI Avatars",
        link: "/avatar-studio/",
      },
      {
        name: "AI Photo Distribution",
        link: "/smart-photo-distribution-for-seamless-events/",
      },
    ],
    showType: "Trade Show",
    attendees: "200+",
    heroImage: (
      <StaticImage
        src="../../images/case-study-covers/richmond.png"
        alt="Event"
        className="w-full h-full object-cover rounded-3xl"
        formats={["auto", "webp", "avif"]}
        width={1200}
      />
    ),
    testimonial: {
      avatar: (
        <StaticImage
          src="../../images/user-avatars/jacopo.png"
          alt="Jacopo Honegger"
          className="w-full h-full object-cover rounded-3xl"
          placeholder="blurred"
        />
      ),
      quote:
        "The software is amazing; we had over 90% of attendees join the app and successfully captured group shots of 200+ people instantly.",
      name: "Jacopo Honegger ",
      Company: "Richmond Italia",
    },
  },
  {
    logo: (
      <StaticImage
        src="../../images/client-logos/latest/32.png"
        alt="Event Tech Live Logo"
        width={100}
        placeholder="blurred"
      />
    ),
    title:
      "Event Tech Live speakers amplified their sessions with branded video posts.",
    features: [
      {
        name: "AI Avatars",
        link: "/avatar-studio/",
      },
      {
        name: "Advocacy Posters",
        link: "/create-event-marketing-posters/",
      },
    ],
    showType: "Trade Show",
    attendees: "4K+",
    heroImage: (
      <StaticImage
        src="../../images/case-study-covers/event-tech.png"
        alt="Event"
        className="w-full h-full object-cover rounded-3xl"
        formats={["auto", "webp", "avif"]}
        width={1200}
      />
    ),
    testimonial: {
      avatar: (
        <StaticImage
          src="../../images/user-avatars/adam-parry.png"
          alt="Adam Parry"
          className="w-full h-full object-cover rounded-3xl"
          placeholder="blurred"
        />
      ),
      quote:
        "Premagic’s advocacy posters for video sessions turned our speaker lineup into a massive organic marketing engine. It gave our speakers a seamless, branded way to amplify their sessions and drove incredible visibility across social media.",
      name: "Adam Parry",
      Company: "Event Tech Live",
    },
  },
  {
    logo: (
      <StaticImage
        src="../../images/client-logos/latest/informa.png"
        alt="Informa Logo"
        width={150}
        placeholder="blurred"
      />
    ),
    title:
      "Informa Connect amplified sponsor visibility with 2,300+ organic advocates ",
    features: [
      {
        name: "AI Photo Distribution",
        link: "/smart-photo-distribution-for-seamless-events/",
      },
      {
        name: "Sponsor Module",
        link: "/sponsored-content/",
      },
    ],
    showType: "Trade Show",
    attendees: "10K+",
    heroImage: (
      <StaticImage
        src="../../images/case-study-covers/middle-east.png"
        alt="Event"
        className="w-full h-full object-cover rounded-3xl"
        formats={["auto", "webp", "avif"]}
        width={800}
      />
    ),
    testimonial: {
      avatar: (
        <StaticImage
          src="../../images/user-avatars/amar.png"
          alt="Amar Daginawala"
          className="w-full h-full object-cover rounded-3xl"
          placeholder="blurred"
        />
      ),
      quote:
        "This was the most visible sponsor branding we’ve done. It wasn’t just logo placement, it was measurable interaction and it kept working even after the event ended.”",
      name: "Amar Daginawala",
      Company: " Informa Connect",
    },
  },
]

const EventCard = ({
  i,
  caseStudy,
  progress,
  range,
  targetScale,
  isMobileUp,
}) => {
  const container = useRef(null)
  const scale = useTransform(progress, range, [1, targetScale])

  const isLastCard = i === caseStudyData.length - 1

  const brightnessVal = useTransform(
    progress,
    [range[0], range[0] + (range[1] - range[0]) * 0.6, range[1]],
    isLastCard ? [1, 1, 1] : [1, 1, 0.7]
  )
  const filterVal = useTransform(brightnessVal, (b) => `brightness(${b})`)

  const y = useTransform(progress, range, [0, -40])
  const TOP = isMobileUp ? 40 : 120
  const TOP_OFFSET = isMobileUp ? 24 : 10

  const { scrollYProgress } = useScroll({
    target: container,
    offset: ["start end", "start start"],
  })
  const imageScale = useTransform(scrollYProgress, [0, 1], [1.5, 1], {
    clamp: true,
  })

  return (
    <div
      ref={container}
      style={{ top: `${TOP + i * TOP_OFFSET}px`, zIndex: i }}
      className="sticky w-full flex justify-center md:pt-10  pb-[100px] last:pb-0 "
    >
      <motion.div
        style={{
          scale,
          filter: filterVal,
          top: `${i * 24}px`,
          y,
        }}
        className="bg-white rounded-[30px] md:rounded-[36px] shadow-xl flex flex-col lg:flex-row gap-6 mb-10 last:mb-0 origin-top min-h-[520px] md:min-h-[580px]"
      >
        <div className="flex-1 flex flex-col justify-between px-8 py-10 pb-0 md:pb-10 h-full">
          <div className="flex-1">
            <div className="w-[90px] md:w-40 mb-4 2xl:w-[150px]">
              {caseStudy?.logo}
            </div>
            <h3 className="text-xl md:text-[28px] 2xl:text-[36px] font-semibold text-gray-900 leading-snug">
              {caseStudy?.title}
            </h3>

            <div className="flex gap-3 mt-5 flex-col md:flex-row">
              {caseStudy?.features?.map((feature) => (
                <Chip
                  text={feature.name}
                  link={feature.link}
                  size={isMobileUp ? "md" : "sm"}
                />
              ))}
            </div>
          </div>

          <div className="bg-[#F7F5FF] rounded-3xl p-[36px]  mt-8 hidden md:block">
            <img
              src={quote}
              alt="Quote"
              className="mb-6 hidden md:block"
              width={40}
            />
            <p className="text-sm md:text-lg 2xl:text-xl text-black text-md font-secondary mb-6">
              {caseStudy?.testimonial?.quote}
            </p>

            <div className="flex items-center gap-3 mt-4">
              {caseStudy?.testimonial?.avatar && (
                <div className="w-10 h-10 rounded-full bg-gray-400">
                  {caseStudy?.testimonial?.avatar}
                </div>
              )}
              <div>
                <p className="text-sm md:text-md font-medium">
                  {caseStudy?.testimonial?.name}
                </p>
                <p className="text-sm text-gray-700">
                  {caseStudy?.testimonial?.Company}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="md:w-[55%] 2xl:w-[50%] w-full p-4 flex pt-0 md:pt-4">
          <div className="relative rounded-2xl overflow-hidden h-full">
            <div className="absolute top-4 right-2 w-full md:w-fit md:right-4 flex justify-center md:justify-end gap-1 md:gap-2 z-10">
              <span className="flex items-center bg-white font-semibold backdrop-blur-sm text-xs md:text-sm text-gray-900 border border-white/10 py-2 px-4 rounded-full">
                <small className="inline-block mr-2">
                  <LuUserRound size={18} />
                </small>
                {caseStudy.attendees} Attendees
              </span>
              <span className="flex items-center bg-white font-semibold backdrop-blur-sm text-xs md:text-sm text-gray-900 border border-white/10 py-2 px-4 rounded-full">
                <small className="inline-block mr-2">
                  <RiArtboardLine size={18} />
                </small>
                {caseStudy?.showType}
              </span>
            </div>
            <motion.div style={{ scale: imageScale }} className="flex h-full ">
              {caseStudy?.heroImage}
            </motion.div>

            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-3xl"></div>

            {isMobileUp && (
              <div className="absolute bottom-4 right-4">
                <Chip
                  text="I'm Interested"
                  variant="secondary"
                  size={isMobileUp ? "lg" : "md"}
                  link={"/request-a-demo/"}
                />
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default function RealEventsSection() {
  const [showCTA, setShowCTA] = useState(false)
  const container = useRef(null)
  const headingRef = useRef(null)
  const isInView = useInView(container, { margin: "-200px" })
  const { width } = useWindowDimensions()
  const isMobileUp = width >= 768

  const { scrollYProgress } = useScroll({
    target: container,
    offset: ["start start", "end end"],
  })

  useEffect(() => {
    const unsubscribe = scrollYProgress.on("change", (v) => {
      if (v > 0.03 && v < 0.95) {
        setShowCTA(true)
      } else {
        setShowCTA(false)
      }
    })

    return () => unsubscribe()
  }, [scrollYProgress])

  return (
    <section ref={container} className="relative md:pb-10 pb-0 pt-6 md:pt-12">
      <div className="container mx-auto px-6 md:px-0 py-6">
        <div ref={headingRef} className="mb-4">
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
            className="text-[36px] md:text-6xl font-semibold text-gray-900"
          >
            Real Events. <br className="block md:hidden" /> Real Campaigns.
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
            className="text-base md:text-lg md:mt-8 mt-4 text-black max-w-lg"
          >
            Pre-event hype. During-event buzz. Post-event longevity. Here's what
            event marketers are building with Premagic.
          </motion.p>
        </div>
        <div
          ref={container}
          style={{ height: `${caseStudyData.length * 100}vh` }}
        >
          {caseStudyData?.map((caseStudy, idx) => {
            const targetScale = 1 - (caseStudyData.length - 1 - idx) * 0.08
            return (
              <EventCard
                i={idx}
                caseStudy={caseStudy}
                progress={scrollYProgress}
                range={[idx / caseStudyData.length, 1]}
                targetScale={targetScale}
                isMobileUp={isMobileUp}
              />
            )
          })}
        </div>

        {/* <div className="realative z-10">
          <Button
            to="/request-a-demo/"
            variant="secondary"
            className="!border-white !text-white hover:bg-white/50"
          >
            Explore More Campaign Ideas
          </Button>
        </div> */}
      </div>
      <div>
        {!isMobileUp && (
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={showCTA ? { y: 0, opacity: 1 } : { y: 80, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed bottom-0 bg-[linear-gradient(0deg,rgba(0,0,0,1)_0%,rgba(255,255,255,0)_100%)] py-4 pt-20 z-50 w-full flex justify-center"
          >
            <Chip
              text="I'm Interested"
              variant="secondary"
              size="md"
              link={"/request-a-demo/"}
            />
          </motion.div>
        )}
      </div>
    </section>
  )
}
